/**
 * Preset A — "left-pinned": classic always-visible sidebar with full labels.
 * Reminiscent of NXG / FXCRM admin dashboards.
 */
import { useState } from "react";
import { Outlet } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Brand } from "./Brand";
import { Topbar } from "./Topbar";
import { SidebarLink } from "./SidebarLink";
import { adminNav } from "./nav";
import { useFilteredAdminNav } from "@/hooks/useEnabledNav";
import { api, unwrap } from "@/lib/api";

export function AdminLayoutA() {
  const qc = useQueryClient();
  const filteredNav = useFilteredAdminNav();
  const [navOpen, setNavOpen] = useState(false);
  const prefetch = (to: string) => {
    if (to === "/admin/dashboard") {
      qc.prefetchQuery({
        queryKey: ["dashboard-summary"],
        queryFn: async () => unwrap((await api.get("/admin/dashboard/summary")).data),
        staleTime: 30_000,
      });
    }
  };

  return (
    <div className="min-h-screen bg-bg flex">
      {navOpen && (
        <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setNavOpen(false)} aria-hidden />
      )}
      <aside
        className={`fixed inset-y-0 left-0 z-50 h-screen w-64 shrink-0 border-r border-border bg-bg-subtle flex flex-col transition-transform duration-200 lg:sticky lg:top-0 lg:translate-x-0 lg:z-auto lg:bg-bg-subtle/50 ${navOpen ? "translate-x-0" : "-translate-x-full"}`}
      >
        <div className="flex h-16 items-center px-5 border-b border-border">
          <Brand />
        </div>
        <nav className="flex-1 overflow-y-auto p-3 space-y-0.5" onClick={() => setNavOpen(false)}>
          {filteredNav.map((item) => (
            <SidebarLink key={item.label} item={item} prefetch={prefetch} />
          ))}
        </nav>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <Topbar onMenuToggle={() => setNavOpen(true)} />
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1600px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
